'use client'

import { type ComponentProps } from 'react'
import clsx from 'clsx'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { arrowStyles, content, trigger } from './Tooltip.css'

type RootProps = ComponentProps<typeof TooltipPrimitive.Root>
const TooltipRoot = ({ children, ...props }: RootProps) => {
  return (
    <TooltipPrimitive.Provider>
      <TooltipPrimitive.Root delayDuration={0} {...props}>
        {children}
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  )
}

type TriggerProps = ComponentProps<typeof TooltipPrimitive.Trigger>
const TooltipTrigger = ({ className, children, ...props }: TriggerProps) => {
  return (
    <TooltipPrimitive.Trigger
      className={clsx(trigger, className)}
      // This avoids auto-opening the tooltip when it is placed in an opening Dialog.
      // See https://github.com/radix-ui/primitives/issues/2248
      onFocusCapture={(e) => e.stopPropagation()}
      {...props}
    >
      {children}
    </TooltipPrimitive.Trigger>
  )
}

type ContentProps = ComponentProps<typeof TooltipPrimitive.Content> & {
  arrow?: boolean
  portal?: boolean
}
const TooltipContent = ({ className, children, arrow = false, portal = true, ...props }: ContentProps) => {
  const Content = (
    <TooltipPrimitive.Content className={clsx(content, className)} sideOffset={5} {...props}>
      {children}
      {arrow && (
        <TooltipPrimitive.Arrow className={arrowStyles} asChild>
          <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M0 0H12L7.41421 4.58579C6.63317 5.36683 5.36684 5.36684 4.58579 4.58579L0 0Z"
              fill="currentColor"
            />
          </svg>
        </TooltipPrimitive.Arrow>
      )}
    </TooltipPrimitive.Content>
  )

  if (!portal) return Content

  return <TooltipPrimitive.Portal>{Content}</TooltipPrimitive.Portal>
}

export const Tooltip = {
  Root: TooltipRoot,
  Trigger: TooltipTrigger,
  Content: TooltipContent,
  Arrow: TooltipPrimitive.Arrow,
}
